import React, { useEffect, useState } from "react";
import { fetchRegions, fetchCitiesByRegion } from "../../../../services/geoService";
import {
  listSuppliers,
  createSupplier,
  type SupplierListItem,
  type CreateSupplierBody,
} from "../../../../services/suppliersService";

type SupplierModalProps = {
  open: boolean;
  onClose: () => void;

  // When user selects from search or successfully creates supplier
  onSelectSupplier: (supplier: SupplierListItem, fromCreate?: boolean) => void;
};

export default function SupplierModal({ open, onClose, onSelectSupplier }: SupplierModalProps) {
  const [mode, setMode] = useState<"search" | "create">("search");

  // search state
  const [searchCr, setSearchCr] = useState("");
  const [searchResults, setSearchResults] = useState<SupplierListItem[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // create state
  const [createForm, setCreateForm] = useState<CreateSupplierBody>({
    name: "",
    email: "",
    phone: "",
    isSupplier: true,
    national_ID: "",
    commercial_Registration_No: "",
    city_Id: 0,
    state_Id: 0,
    isMaintainanceContractor: true,
    creation_Platform: "donation_web_portal",
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // geo
  const [regions, setRegions] = useState<{ id: number; name: string }[]>([]);
  const [cities, setCities] = useState<{ id: number; name: string }[]>([]);
  const [geoLoading, setGeoLoading] = useState(false);

  // Reset modal state each time it opens
  useEffect(() => {
    if (!open) return;
    setMode("search");
    setSearchCr("");
    setSearchResults([]);
    setSearchLoading(false);

    setCreateError(null);
    setCreateLoading(false);
    setCreateForm({
      name: "",
      email: "",
      phone: "",
      isSupplier: true,
      national_ID: "",
      commercial_Registration_No: "",
      city_Id: 0,
      state_Id: 0,
      isMaintainanceContractor: true,
      creation_Platform: "donation_web_portal",
    });
    setCities([]);
  }, [open]);

  // Load regions when modal is open & create mode
  useEffect(() => {
    if (!open || mode !== "create") return;
    if (regions.length > 0) return;

    setGeoLoading(true);
    fetchRegions()
      .then((rows) => setRegions(Array.isArray(rows) ? rows : []))
      .catch(() => setRegions([]))
      .finally(() => setGeoLoading(false));
  }, [open, mode, regions.length]);

  // Load cities when region changes (create mode)
  useEffect(() => {
    if (!open || mode !== "create") return;

    const regionId = createForm.state_Id;
    if (!regionId || regionId <= 0) {
      setCities([]);
      return;
    }

    let cancelled = false;
    setGeoLoading(true);
    fetchCitiesByRegion(regionId)
      .then((rows) => {
        if (!cancelled) setCities(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setCities([]);
      })
      .finally(() => {
        if (!cancelled) setGeoLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, mode, createForm.state_Id]);

  const handleSearch = async () => {
    setSearchLoading(true);
    setSearchResults([]);
    try {
      const list = await listSuppliers({
        commercialRegistrationNo: searchCr.trim() || undefined,
        pageNo: 1,
        pageSize: 25,
      });
      setSearchResults(list);
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError(null);

    if (!createForm.name?.trim()) {
      setCreateError("اسم الشركة مطلوب.");
      return;
    }

    // التحقق من وجود السجل التجاري مسبقاً قبل الإنشاء
    const crTrim = createForm.commercial_Registration_No?.trim();
    if (crTrim) {
      try {
        const existing = await listSuppliers({
          commercialRegistrationNo: crTrim,
          pageNo: 1,
          pageSize: 5,
        });
        if (existing.length > 0) {
          setCreateError("رقم السجل التجاري مسجل مسبقاً. يرجى البحث عنه واختياره من النتائج.");
          return;
        }
      } catch {
        /* إذا فشل البحث، نتابع الإنشاء */
      }
    }

    try {
      setCreateLoading(true);

      const payload: CreateSupplierBody = {
        name: createForm.name.trim(),
        isSupplier: true,
        isMaintainanceContractor: true,
        creation_Platform: "donation_web_portal",
      };
      const emailTrim = createForm.email?.trim();
      const phoneTrim = createForm.phone?.trim();
      const nationalTrim = createForm.national_ID?.trim();
      const crTrim = createForm.commercial_Registration_No?.trim();
      if (emailTrim) payload.email = emailTrim;
      if (phoneTrim) payload.phone = phoneTrim;
      if (nationalTrim) payload.national_ID = nationalTrim;
      if (crTrim) payload.commercial_Registration_No = crTrim;
      if (createForm.city_Id && createForm.city_Id > 0) payload.city_Id = createForm.city_Id;
      if (createForm.state_Id && createForm.state_Id > 0) payload.state_Id = createForm.state_Id;

      const resp = await createSupplier(payload);

      // عند HTTP 200 نعتبر الإنشاء ناجحاً — لا نعرض خطأ أبداً
      const r: any = resp && typeof resp === "object" ? (resp?.result ?? resp) : {};
      const extractId = (obj: any): number | undefined => {
        if (!obj || typeof obj !== "object") return undefined;
        const val =
          obj.id ?? obj.Id ?? obj.supplier_id ?? obj.SupplierId ?? obj.Supplier_Id ??
          obj.partner_id ?? obj.Partner_Id ?? obj.PartnerId ??
          obj.partnerId ?? obj.CommercialPartnerId;
        const n = val != null ? Number(val) : NaN;
        return Number.isFinite(n) && n > 0 ? n : undefined;
      };

      let id =
        extractId(r) ??
        extractId(r?.data) ??
        extractId(r?.supplier) ??
        extractId(r?.Supplier) ??
        extractId(r?.partner) ??
        extractId(r?.Partner) ??
        extractId(resp) ??
        extractId(resp?.data) ??
        extractId(resp?.supplier) ??
        extractId(resp?.Supplier) ??
        extractId(resp?.result) ??
        extractId(resp?.result?.data);

      // Fallback: البحث عن المتعهد المُنشأ للحصول على المعرف (يظهر في البحث بعد الإنشاء)
      if (id == null) {
        const searchName = createForm.name.trim();
        const searchCr = createForm.commercial_Registration_No?.trim();
        const doSearch = async (): Promise<SupplierListItem[]> => {
          if (searchCr) {
            const byCr = await listSuppliers({
              commercialRegistrationNo: searchCr,
              pageNo: 1,
              pageSize: 10,
            });
            if (byCr.length > 0) return byCr;
          }
          if (searchName) {
            return listSuppliers({ name: searchName, pageNo: 1, pageSize: 10 });
          }
          return [];
        };
        try {
          let list = await doSearch();
          if (list.length === 0) {
            await new Promise((resolve) => setTimeout(resolve, 600));
            list = await doSearch();
          }
          const normalize = (x: string | undefined) => String(x ?? "").trim().toLowerCase();
          const found = list?.find(
            (s) => {
              const sid = s.id ?? (s as any)?.Id ?? (s as any)?.supplier_id ?? (s as any)?.SupplierId;
              const validId = sid != null && Number(sid) > 0;
              const itemCr = normalize(s.commercialRegistrationNo ?? (s as any)?.commercial_Registration_No);
              const crMatch = !searchCr || itemCr === normalize(searchCr);
              const itemName = normalize(s.name ?? (s as any)?.Name);
              const nameMatch = !searchName || itemName === normalize(searchName) || itemName.includes(normalize(searchName));
              return validId && crMatch && nameMatch;
            }
          );
          if (found) {
            const sid = found.id ?? (found as any)?.Id ?? (found as any)?.supplier_id ?? (found as any)?.SupplierId;
            id = sid != null ? Number(sid) : undefined;
            if (id != null) {
              const supplierFromList: SupplierListItem = {
                id,
                name: found.name ?? (found as any)?.Name ?? createForm.name.trim(),
                email: found.email ?? (found as any)?.Email ?? createForm.email?.trim() ?? "",
                phone: found.phone ?? (found as any)?.Phone ?? createForm.phone?.trim() ?? "",
                commercialRegistrationNo:
                  found.commercialRegistrationNo ??
                  (found as any)?.commercial_Registration_No ??
                  (found as any)?.CommercialRegistrationNo ??
                  createForm.commercial_Registration_No?.trim() ??
                  "",
              };
              onSelectSupplier(supplierFromList, true);
              onClose();
              return;
            }
          }
        } catch {
          /* ignore */
        }
      }

      const supplier: SupplierListItem = {
        id,
        name: createForm.name.trim(),
        email: createForm.email?.trim() ?? "",
        phone: createForm.phone?.trim() ?? "",
        commercialRegistrationNo: createForm.commercial_Registration_No?.trim() ?? "",
        city_Id: createForm.city_Id,
        state_Id: createForm.state_Id,
      } as SupplierListItem;

      onSelectSupplier(supplier, true);
      onClose();
    } catch (err: any) {
      // عند فشل الطلب، قد يكون Odoo أنشأ المتعهد — جرّب البحث الاحتياطي
      const searchCr = createForm.commercial_Registration_No?.trim();
      const searchName = createForm.name.trim();
      if (searchCr || searchName) {
        try {
          const doSearch = async (): Promise<SupplierListItem[]> => {
            if (searchCr) {
              const byCr = await listSuppliers({
                commercialRegistrationNo: searchCr,
                pageNo: 1,
                pageSize: 10,
              });
              if (byCr.length > 0) return byCr;
            }
            if (searchName) {
              return listSuppliers({ name: searchName, pageNo: 1, pageSize: 10 });
            }
            return [];
          };
          let list = await doSearch();
          if (list.length === 0) {
            await new Promise((resolve) => setTimeout(resolve, 600));
            list = await doSearch();
          }
          const normalize = (x: string | undefined) => String(x ?? "").trim().toLowerCase();
          const found = list?.find(
            (s) => {
              const sid = s.id ?? (s as any)?.Id ?? (s as any)?.supplier_id ?? (s as any)?.SupplierId;
              const validId = sid != null && Number(sid) > 0;
              const itemCr = normalize(s.commercialRegistrationNo ?? (s as any)?.commercial_Registration_No);
              const crMatch = !searchCr || itemCr === normalize(searchCr);
              const itemName = normalize(s.name ?? (s as any)?.Name);
              const nameMatch = !searchName || itemName === normalize(searchName) || itemName.includes(normalize(searchName));
              return validId && crMatch && nameMatch;
            }
          );
          if (found) {
            const sid = found.id ?? (found as any)?.Id ?? (found as any)?.supplier_id ?? (found as any)?.SupplierId;
            const id = sid != null ? Number(sid) : undefined;
            if (id != null) {
              const supplierFromList: SupplierListItem = {
                id,
                name: found.name ?? (found as any)?.Name ?? createForm.name.trim(),
                email: found.email ?? (found as any)?.Email ?? createForm.email?.trim() ?? "",
                phone: found.phone ?? (found as any)?.Phone ?? createForm.phone?.trim() ?? "",
                commercialRegistrationNo:
                  found.commercialRegistrationNo ??
                  (found as any)?.commercial_Registration_No ??
                  (found as any)?.CommercialRegistrationNo ??
                  createForm.commercial_Registration_No?.trim() ??
                  "",
              };
              onSelectSupplier(supplierFromList, true);
              onClose();
              return;
            }
          }
        } catch {
          /* ignore */
        }
      }
      setCreateError(err?.message ?? "حدث خطأ أثناء إنشاء الشركة.");
    } finally {
      setCreateLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" role="dialog" aria-modal="true" aria-label="متعهد الصيانة">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-neutral-900">متعهد الصيانة</h3>
          <button type="button" className="text-neutral-500 hover:text-neutral-700 text-2xl leading-none" onClick={onClose} aria-label="إغلاق">×</button>
        </div>

        <div className="p-5 flex gap-2 border-b border-neutral-100">
          <button
            type="button"
            className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-medium ${mode === "search" ? "bg-sa-100 text-sa-800 border border-sa-200" : "bg-neutral-50 text-neutral-600 border border-transparent hover:bg-neutral-100"}`}
            onClick={() => setMode("search")}
          >
            بحث عن متعهد صيانة
          </button>
          <button
            type="button"
            className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-medium ${mode === "create" ? "bg-sa-100 text-sa-800 border border-sa-200" : "bg-neutral-50 text-neutral-600 border border-transparent hover:bg-neutral-100"}`}
            onClick={() => setMode("create")}
          >
            انشاء متعهد صيانة
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {mode === "search" ? (
            <>
              <div className="space-y-3 mb-4">
                <input
                  type="text"
                  placeholder="رقم السجل التجاري"
                  value={searchCr}
                  onChange={(e) => setSearchCr(e.target.value)}
                  className="block w-full rounded-xl border border-neutral-300 bg-white px-3 py-2 text-neutral-900 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
                />
              </div>

              <button
                type="button"
                className="w-full rounded-xl bg-sa-600 text-white px-4 py-2.5 text-sm font-medium hover:bg-sa-700 focus:outline-none focus:ring-2 focus:ring-sa-500 disabled:opacity-60"
                onClick={handleSearch}
                disabled={searchLoading || !searchCr.trim()}
              >
                {searchLoading ? "جاري البحث..." : "بحث"}
              </button>

              <div className="mt-4 space-y-2 max-h-48 overflow-y-auto">
                {searchResults.length === 0 && !searchLoading && (
                  <p className="text-sm text-neutral-500">أدخل رقم السجل التجاري لمتعهد الصيانة ثم اضغط بحث.</p>
                )}

                {searchResults.map((s) => (
                  <button
                    key={s.id ?? s.name ?? Math.random()}
                    type="button"
                    className="w-full text-right rounded-xl border border-neutral-200 bg-neutral-50 hover:bg-sa-50 hover:border-sa-200 px-4 py-3 text-sm text-neutral-800"
                    onClick={() => {
                      onSelectSupplier(s);
                      onClose();
                    }}
                  >
                    <span className="font-medium">{s.name ?? "—"}</span>
                    {(s.email || s.phone) && (
                      <span className="block text-neutral-500 text-xs mt-1">{[s.email, s.phone].filter(Boolean).join(" · ")}</span>
                    )}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <form onSubmit={handleCreate} className="space-y-3">
              {createError && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-sm">{createError}</div>
              )}

              <input
                type="text"
                placeholder="اسم الشركة *"
                value={createForm.name}
                onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                className="block w-full rounded-xl border border-neutral-300 bg-white px-3 py-2 text-neutral-900 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
                required
              />

              <input
                type="text"
                placeholder="البريد الإلكتروني"
                value={createForm.email}
                onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                className="block w-full rounded-xl border border-neutral-300 bg-white px-3 py-2 text-neutral-900 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
              />

              <input
                type="text"
                placeholder="رقم الجوال"
                value={createForm.phone}
                onChange={(e) => setCreateForm((f) => ({ ...f, phone: e.target.value }))}
                className="block w-full rounded-xl border border-neutral-300 bg-white px-3 py-2 text-neutral-900 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
              />

              <input
                type="text"
                placeholder="رقم الهوية الوطنية / الاقامة "
                value={createForm.national_ID}
                onChange={(e) => setCreateForm((f) => ({ ...f, national_ID: e.target.value }))}
                className="block w-full rounded-xl border border-neutral-300 bg-white px-3 py-2 text-neutral-900 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
              />

              <input
                type="text"
                placeholder="رقم السجل التجاري"
                value={createForm.commercial_Registration_No}
                onChange={(e) => setCreateForm((f) => ({ ...f, commercial_Registration_No: e.target.value }))}
                className="block w-full rounded-xl border border-neutral-300 bg-white px-3 py-2 text-neutral-900 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
              />

              <div>
                <label className="block text-xs text-neutral-500 mb-1">المنطقة</label>
                <select
                  value={createForm.state_Id || ""}
                  onChange={(e) => {
                    const v = e.target.value ? Number(e.target.value) : 0;
                    setCreateForm((f) => ({ ...f, state_Id: v, city_Id: 0 }));
                  }}
                  disabled={geoLoading}
                  className="block w-full rounded-xl border border-neutral-300 bg-white px-3 py-2 text-neutral-900 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
                >
                  <option value="">— اختر المنطقة —</option>
                  {regions.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name ?? r.id}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs text-neutral-500 mb-1">المدينة</label>
                <select
                  value={createForm.city_Id || ""}
                  onChange={(e) => setCreateForm((f) => ({ ...f, city_Id: e.target.value ? Number(e.target.value) : 0 }))}
                  disabled={geoLoading || !createForm.state_Id}
                  className="block w-full rounded-xl border border-neutral-300 bg-white px-3 py-2 text-neutral-900 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
                >
                  <option value="">— اختر المدينة —</option>
                  {cities.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name ?? c.id}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="submit"
                className="w-full rounded-xl bg-sa-600 text-white px-4 py-2.5 text-sm font-medium hover:bg-sa-700 focus:outline-none focus:ring-2 focus:ring-sa-500 disabled:opacity-60"
                disabled={createLoading}
              >
                {createLoading ? "جاري الإنشاء..." : "إنشاء"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}